import { useContext, useEffect, useRef, useState } from "react";
import { SmartDPMapMarker } from "app/pages/SmartDP/Map/SmartDPMapMarker";
import useSupercluster from "use-supercluster";
import classNames from "classnames";
import GoogleMap from "google-map-react";
import appGlobalsContext from "app/AppGlobalsContext";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import "./ProductsMap.scss";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { DEFAULT_MAP_ZOOM } from "app/constants";

const Marker = ({ children }) => children;

const ProductsMap = ({
	center = {},
	onMarkerClick = () => {},
	points = [],
	googleAPIKey,
	itemSelected,
	zoom,
	onMapChange = () => {},
	maxZoom = 10,
}) => {
	const { isRTL } = useContext(appGlobalsContext);

	const [bounds, setBounds] = useState(null);
	const [currentZoom, setZoom] = useState(zoom);

	const mapRef = useRef();

	const { clusters, supercluster } = useSupercluster({
		points,
		bounds,
		zoom: currentZoom,
		options: { radius: 75, maxZoom: maxZoom },
	});

	const clusterClassNames = leaves => {
		const names = leaves.map(leave => leave.properties.name);

		return classNames({
			"products-map__cluster-marker": true,
			"products-map__cluster-marker--active": names.includes(itemSelected),
		});
	};

	useEffect(() => {
		// mettre à jour le zoom si il change depuis l'extérieur (hover sur les products cards par exemple)
		setZoom(zoom);
	}, [zoom]);

	if (!googleAPIKey) {
		return null;
	}

	return (
		<GoogleMap
			resetBoundsOnResize={true}
			options={() => ({
				scrollwheel: true,
				clickableIcons: false,
				fullscreenControl: false,
				scaleControl: true,
				gestureHandling: "greedy",
				zoomControl: true,
				zoomControlOptions: {
					position: isRTL
						? window?.google?.maps?.ControlPosition.LEFT_TOP
						: window?.google?.maps?.ControlPosition.RIGHT_TOP,
				},
			})}
			onChange={({ zoom, bounds }) => {
				onMapChange(zoom);
				setBounds([bounds.nw?.lng, bounds.se?.lat, bounds.se?.lng, bounds.nw?.lat]);
			}}
			bootstrapURLKeys={{
				key: googleAPIKey,
			}}
			hoverDistance={30}
			center={center}
			zoom={currentZoom}
			defaultZoom={DEFAULT_MAP_ZOOM}
			yesIWantToUseGoogleMapApiInternals
			onGoogleApiLoaded={({ map }) => {
				mapRef.current = map;
			}}
		>
			{clusters.map(cluster => {
				const [longitude, latitude] = cluster.geometry.coordinates;
				const {
					cluster: isCluster,
					point_count: pointCount,
					name,
					productUrl,
					isFlashsale,
					total,
				} = cluster.properties;

				if (isCluster) {
					return (
						<Marker key={`cluster-${cluster.id}`} lat={latitude} lng={longitude}>
							<div
								className={clusterClassNames(
									supercluster.getLeaves(cluster.id, Infinity)
								)}
								style={{
									width: `${10 + (pointCount / points.length) * 20}px`,
									height: `${10 + (pointCount / points.length) * 20}px`,
								}}
								onClick={() => {
									if (mapRef?.current) {
										const expansionZoom = Math.min(
											supercluster.getClusterExpansionZoom(cluster.id),
											20
										);
										mapRef.current.setZoom(expansionZoom);
										mapRef.current.panTo({
											lat: latitude,
											lng: longitude,
										});
									}
								}}
							>
								<Typography variant={TYPOGRAPHY_VARIANTS.SMALL} isBold>
									{pointCount}
								</Typography>
							</div>
						</Marker>
					);
				}
				return (
					<SmartDPMapMarker
						key={name}
						lat={latitude}
						lng={longitude}
						name={name}
						productUrl={productUrl}
						total={total}
						isFlashsale={isFlashsale}
						isActive={itemSelected === name}
						onMarkerClick={onMarkerClick}
					/>
				);
			})}
		</GoogleMap>
	);
};

ProductsMap.propTypes = {
	center: PropTypes.shape({
		lat: PropTypes.number,
		lng: PropTypes.number,
	}),
	onMarkerClick: PropTypes.func,
	onMapChange: PropTypes.func,
	points: PropTypes.array,
	googleAPIKey: PropTypes.string.isRequired,
	itemSelected: PropTypes.string,
	zoom: PropTypes.number,
	maxZoom: PropTypes.number,
};

const mapStateToProps = state => ({
	googleAPIKey: state.envVars.GOOGLE_API_KEY,
});

export default connect(mapStateToProps)(ProductsMap);
